import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import usePermission from '../../custom/usePermission';
import { useLocation, useHistory } from 'react-router-dom';
import { AuthContext } from '../../Header/auth - context';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import './Vesti.css';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* ── Helpers ─────────────────────────────────────────────────── */
const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const diff = (Date.now() - d) / 1000;
  if (diff < 60)     return 'upravo';
  if (diff < 3600)   return `pre ${Math.floor(diff / 60)} min`;
  if (diff < 86400)  return `pre ${Math.floor(diff / 3600)} h`;
  if (diff < 604800) return `pre ${Math.floor(diff / 86400)} dana`;
  return d.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const initials = (ime, prezime) =>
  `${ime?.[0] || ''}${prezime?.[0] || ''}`.toUpperCase();

const isImageUrl = (url) =>
  url && (
    /\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?.*)?$/i.test(url.trim()) ||
    /^data:image\//i.test(url.trim())
  );

/* Parse tekst — either JSON block array or legacy plain string */
const parseBlocks = (tekst) => {
  try {
    const parsed = JSON.parse(tekst);
    if (Array.isArray(parsed)) return parsed;
  } catch {}
  return [{ type: 'text', value: tekst || '' }];
};

const URL_RE = /(https?:\/\/\S+)/g;
const renderTextInline = (text) => {
  if (!text) return null;
  const out = [];
  let last = 0;
  let m;
  const re = /(https?:\/\/\S+)/g;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last)
      out.push(<span key={`t${last}`}>{text.slice(last, m.index)}</span>);
    out.push(
      <a key={`u${m.index}`} href={m[0]} target="_blank" rel="noopener noreferrer" className="vest-link">
        {m[0]}
      </a>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(<span key={`t${last}`}>{text.slice(last)}</span>);
  return out.length ? out : text;
};

/* Render blocks for display */
const renderBlocks = (tekst) => {
  const blocks = parseBlocks(tekst);
  return blocks.map((block, i) => {
    if (block.type === 'image' && block.value) {
      return (
        <img
          key={i}
          src={block.value}
          alt=""
          className="vest-block-image"
          onError={e => { e.target.style.display = 'none'; }}
        />
      );
    }
    if (block.type === 'text' && block.value) {
      return (
        <div key={i} className="vest-block-text">
          {renderTextInline(block.value)}
        </div>
      );
    }
    return null;
  });
};

/* Paste image from clipboard — calls onImage(base64) */
const clipboardImage = (e, onImage) => {
  const items = e.clipboardData?.items;
  if (!items) return false;
  for (const item of items) {
    if (item.type.startsWith('image/')) {
      e.preventDefault();
      const file = item.getAsFile();
      if (!file) return true;
      const reader = new FileReader();
      reader.onload = ev => onImage(ev.target.result);
      reader.readAsDataURL(file);
      return true;
    }
  }
  return false;
};

/* ── Block editor ────────────────────────────────────────────── */
const BlockEditor = ({ blocks, onChange }) => {
  const { t } = useTranslation();
  const update = (idx, value) =>
    onChange(blocks.map((b, i) => i === idx ? { ...b, value } : b));

  const remove = (idx) =>
    onChange(blocks.filter((_, i) => i !== idx));

  const add = (type) =>
    onChange([...blocks, { type, value: '' }]);

  const onTextPaste = (e, idx) => {
    clipboardImage(e, (base64) => {
      const next = [...blocks];
      next.splice(idx + 1, 0,
        { type: 'image', value: base64 },
        { type: 'text',  value: '' }
      );
      onChange(next);
    });
  };

  const onImagePaste = (e, idx) => {
    clipboardImage(e, (base64) => update(idx, base64));
  };

  return (
    <div className="block-editor">
      {blocks.map((block, idx) => (
        <div key={idx} className="block-editor-item">
          {block.type === 'text' ? (
            <InputTextarea
              value={block.value}
              onChange={e => update(idx, e.target.value)}
              onPaste={e => onTextPaste(e, idx)}
              rows={3}
              autoResize
              className="vest-textarea"
              placeholder={t('home.tekst')}
            />
          ) : (
            <div className="block-editor-image-wrap">
              <InputText
                value={isImageUrl(block.value) && block.value.startsWith('data:') ? '' : block.value}
                onChange={e => update(idx, e.target.value)}
                onPaste={e => onImagePaste(e, idx)}
                placeholder={t('home.urlSlike')}
                className="vest-input"
              />
              {block.value && isImageUrl(block.value) && (
                <img
                  src={block.value}
                  alt="preview"
                  className="vest-img-preview"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              )}
            </div>
          )}
          {blocks.length > 1 && (
            <button className="block-remove-btn" onClick={() => remove(idx)} title="Ukloni blok">
              <i className="pi pi-times" />
            </button>
          )}
        </div>
      ))}
      <div className="block-add-row">
        <button className="block-add-btn" onClick={() => add('text')}>
          <i className="pi pi-align-left" /> Tekst
        </button>
        <button className="block-add-btn" onClick={() => add('image')}>
          <i className="pi pi-image" /> Slika
        </button>
      </div>
    </div>
  );
};

/* ── Single card ─────────────────────────────────────────────── */
const VestCard = ({ vest, token, currentUserId, userRole, eskaliran, onDeleted, onEdited }) => {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canVesti = can('VESTI', 'edit');
  const [commentsOpen, setCommentsOpen]     = useState(false);
  const [comments, setComments]             = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [newComment, setNewComment]         = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editing, setEditing]               = useState(false);
  const [editNaslov, setEditNaslov]         = useState(vest.naslov || '');
  const [editBlocks, setEditBlocks]         = useState(() => parseBlocks(vest.tekst));
  const [saving, setSaving]                 = useState(false);

  const canModify    = vest.user_id === currentUserId || canVesti;
  const commentCount = commentsLoaded ? comments.length : (vest.komentar_count || 0);

  const loadComments = () => {
    if (commentsLoaded) return;
    fetch(`${BACKEND}/vesti/komentari/list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ vest_id: vest.id }),
    })
      .then(r => r.json())
      .then(data => {
        setComments(Array.isArray(data) ? data.map(c => ({ ...c, obrisan: c.status === 0 })) : []);
        setCommentsLoaded(true);
      })
      .catch(() => setCommentsLoaded(true));
  };

  const toggleComments = () => {
    setCommentsOpen(prev => { if (!prev) loadComments(); return !prev; });
  };

  const submitComment = () => {
    if (!newComment.trim() || submittingComment) return;
    setSubmittingComment(true);
    fetch(`${BACKEND}/vesti/komentari/add`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ vest_id: vest.id, tekst: newComment.trim() }),
    })
      .then(r => r.json())
      .then(data => {
        if (data?.id) { setComments(prev => [...prev, data]); setNewComment(''); }
        setSubmittingComment(false);
      })
      .catch(() => setSubmittingComment(false));
  };

  const deleteComment = (id) => {
    fetch(`${BACKEND}/vesti/komentari/delete`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ id }),
    }).then(r => { if (r.ok) setComments(prev => prev.map(c => c.id === id ? { ...c, obrisan: true } : c)); });
  };

  const saveEdit = () => {
    const tekst = JSON.stringify(editBlocks.filter(b => b.value?.trim()));
    if (!tekst || tekst === '[]') return;
    setSaving(true);
    fetch(`${BACKEND}/vesti/edit`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ id: vest.id, naslov: editNaslov, tekst, slika_url: null }),
    })
      .then(r => {
        setSaving(false);
        if (r.ok) { onEdited(vest.id, { naslov: editNaslov, tekst }); setEditing(false); }
      })
      .catch(() => setSaving(false));
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditNaslov(vest.naslov || '');
    setEditBlocks(parseBlocks(vest.tekst));
  };

  const handleDelete = () => {
    if (!window.confirm('Obrisati ovu vest?')) return;
    fetch(`${BACKEND}/vesti/delete`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ id: vest.id }),
    }).then(r => { if (r.ok) onDeleted(vest.id); });
  };

  return (
    <div className="vest-card">
      <div className="vest-card-glow" />

      {/* author row */}
      <div className="vest-header-row">
        <div className="vest-avatar">{initials(vest.ime, vest.prezime)}</div>
        <div className="vest-meta">
          <span className="vest-author">{vest.ime} {vest.prezime}</span>
          <span className="vest-date-edited">
            {fmtDate(vest.datum)}{vest.edited ? ' · izmenjeno' : ''}
          </span>
        </div>
        {canModify && !editing && (
          <div className="vest-card-actions">
            <button className="vest-action-btn" onClick={() => setEditing(true)} title="Izmeni">
              <i className="pi pi-pencil" />
            </button>
            <button className="vest-action-btn vest-action-del" onClick={handleDelete} title="Obriši">
              <i className="pi pi-trash" />
            </button>
          </div>
        )}
      </div>

      {/* body or edit form */}
      {editing ? (
        <div className="vest-edit-form">
          <InputText
            value={editNaslov}
            onChange={e => setEditNaslov(e.target.value)}
            placeholder={t('home.naslovOpciono')}
            className="vest-input"
          />
          <BlockEditor blocks={editBlocks} onChange={setEditBlocks} />
          <div className="vest-edit-actions">
            <Button label={t('admin.sacuvaj')} icon="pi pi-check" className="p-button-sm vest-save-btn"
              loading={saving} onClick={saveEdit} />
            <Button label={t('common.cancel')} icon="pi pi-times" className="p-button-sm vest-cancel-btn"
              onClick={cancelEdit} />
          </div>
        </div>
      ) : (
        <div className="vest-body">
          {vest.naslov && <h3 className="vest-title">{vest.naslov}</h3>}
          {renderBlocks(vest.tekst)}
        </div>
      )}

      {/* footer toggle */}
      <div className="vest-footer">
        <button
          className={`vest-comment-toggle${commentsOpen ? ' is-open' : ''}`}
          onClick={toggleComments}
        >
          <i className="pi pi-comments" />
          <span>Komentari{commentCount > 0 ? ` (${commentCount})` : ''}</span>
          <i className={`pi pi-chevron-${commentsOpen ? 'up' : 'down'} vest-chevron`} />
        </button>
      </div>

      {/* comments */}
      {commentsOpen && (
        <div className="vest-comments">
          {comments.map(c => (
            <div key={c.id} className={`vest-komentar${c.obrisan ? ' vk-deleted-wrap' : ''}`}>
              <div className="vk-avatar">{initials(c.ime, c.prezime)}</div>
              <div className="vk-body">
                {c.obrisan ? (
                  <div className="vk-deleted">
                    <i className="pi pi-ban" /> Komentar je obrisan
                  </div>
                ) : (
                  <>
                    <div className="vk-meta">
                      <span className="vk-author">{c.ime} {c.prezime}</span>
                      <span className="vk-date">{fmtDate(c.datum)}{c.edited ? ' · izm.' : ''}</span>
                      {c.user_id === currentUserId && (
                        <button className="vk-del-btn" onClick={() => deleteComment(c.id)} title="Obriši">
                          <i className="pi pi-times" />
                        </button>
                      )}
                    </div>
                    <div className="vk-text">{c.tekst}</div>
                  </>
                )}
              </div>
            </div>
          ))}
          <div className="vest-add-komentar">
            <InputTextarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') submitComment(); }}
              rows={2}
              placeholder={t('home.dodajKomentar')}
              className="vk-input"
              autoResize
            />
            <Button
              icon="pi pi-send"
              className="p-button-sm vk-send-btn"
              onClick={submitComment}
              disabled={!newComment.trim()}
              loading={submittingComment}
            />
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Page ────────────────────────────────────────────────────── */
const Vesti = () => {
  const { t } = useTranslation();
  const auth      = useContext(AuthContext);
  const userData  = useSelector(s => s.user);
  const role      = useSelector(s => s.user_role);
  const eskaliran = useSelector(s => s.eskaliran);
  const location  = useLocation();
  const history   = useHistory();
  const toastRef  = useRef(null);

  const [vesti, setVesti]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [formOpen, setFormOpen]   = useState(false);
  const [formNaslov, setFormNaslov] = useState('');
  const [formBlocks, setFormBlocks] = useState([{ type: 'text', value: '' }]);
  const [submitting, setSubmitting] = useState(false);

  const token         = auth.token;
  const currentUserId = userData?.userId;
  const userRole      = role?.roleId;
  const isEskaliran   = eskaliran?.eskaliran;

  useEffect(() => {
    fetch(`${BACKEND}/vesti`, {
      headers: { Authorization: 'Bearer ' + token },
    })
      .then(r => r.json())
      .then(data => { setVesti(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));

    if (location.state?.openForm) {
      setFormOpen(true);
      history.replace({ pathname: location.pathname, state: {} });
    }
  }, [token]);

  const submitVest = () => {
    const validBlocks = formBlocks.filter(b => b.value?.trim());
    if (!validBlocks.length || submitting) return;
    const tekst = JSON.stringify(validBlocks);
    setSubmitting(true);
    fetch(`${BACKEND}/vesti/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ naslov: formNaslov, tekst, slika_url: null }),
    })
      .then(r => r.json())
      .then(data => {
        setSubmitting(false);
        if (data?.id) {
          setVesti(prev => [{
            id: data.id,
            naslov: formNaslov.trim() || null,
            tekst,
            slika_url: null,
            user_id: currentUserId,
            datum: data.datum,
            edited: false,
            komentar_count: 0,
            ime: userData?.userName,
            prezime: userData?.userSurname,
          }, ...prev]);
          setFormNaslov('');
          setFormBlocks([{ type: 'text', value: '' }]);
          setFormOpen(false);
          toastRef.current?.show({ severity: 'success', summary: 'Vest objavljena!', life: 2500 });
        }
      })
      .catch(() => setSubmitting(false));
  };

  const handleDeleted = (id) => setVesti(prev => prev.filter(v => v.id !== id));
  const handleEdited  = (id, updates) =>
    setVesti(prev => prev.map(v => v.id === id ? { ...v, ...updates, edited: true } : v));

  const hasContent = formBlocks.some(b => b.value?.trim());

  return (
    <div className="vesti-page">
      <Toast ref={toastRef} />
      <div className="vesti-content">

        {/* page header */}
        <div className="vesti-page-header">
          <div className="vesti-title-wrap">
            <i className="pi pi-megaphone vesti-megaphone-icon" />
            <h1 className="vesti-page-title">VESTI KOMPANIJE</h1>
          </div>
          <Button
            label={formOpen ? 'Zatvori' : 'Nova vest'}
            icon={formOpen ? 'pi pi-times' : 'pi pi-plus'}
            className="p-button-sm nova-vest-btn"
            onClick={() => setFormOpen(prev => !prev)}
          />
        </div>

        {/* create form */}
        {formOpen && (
          <div className="nova-vest-panel">
            <p className="nova-vest-heading">Nova objava</p>
            <InputText
              value={formNaslov}
              onChange={e => setFormNaslov(e.target.value)}
              placeholder={t('home.naslovOpciono')}
              className="vest-input"
            />
            <BlockEditor blocks={formBlocks} onChange={setFormBlocks} />
            <div className="nova-vest-actions">
              <Button
                label={t('home.objavi')}
                icon="pi pi-send"
                className="p-button-sm nova-vest-submit"
                loading={submitting}
                onClick={submitVest}
                disabled={!hasContent}
              />
              <Button
                label={t('common.cancel')}
                icon="pi pi-times"
                className="p-button-sm nova-vest-cancel"
                onClick={() => setFormOpen(false)}
              />
            </div>
          </div>
        )}

        {/* feed */}
        {loading ? (
          <div className="vesti-loading">
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#f8cc2e' }} />
          </div>
        ) : vesti.length === 0 ? (
          <div className="vesti-empty">
            <i className="pi pi-inbox" />
            <p>Nema objavljenih vesti</p>
          </div>
        ) : (
          <div className="vesti-feed">
            {vesti.map(vest => (
              <VestCard
                key={vest.id}
                vest={vest}
                token={token}
                currentUserId={currentUserId}
                userRole={userRole}
                eskaliran={isEskaliran}
                onDeleted={handleDeleted}
                onEdited={handleEdited}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Vesti;
